import React from 'react'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { AddShoppingCart } from '@mui/icons-material'
import DietaryRow from '@/components/DietaryListMui'
import { numberFormat } from '@/helpers/numbers'
import ItemCard from './shared/ItemCard'
import { optionsAreValid } from '../ccMenuSpecificHelpers'
import { FragmentType, graphql, useFragment } from '@/generated/gql'

const canteenMenuItemCardUserDataFragment = graphql(`
  fragment CanteenMenuItemCard_UserDataFragment on User {
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

const canteenMenuItemCardMenuItemFragment = graphql(`
  fragment CanteenMenuItemCard_MenuItemFragment on CCMenuItem {
    submenuId
    quantityRemaining
    liveVariation {
      priceCustomerFacing
      varType
    }
    liveMenuItem {
      _id
      name
      image
      dietaries
      allergens
      isCombo
      shortDescription
      subAllergens {
        nuts
        cereals
      }
      options {
        _id
        minQty
        maxQty
        qtyPerPerson
      }
      translations {
        key
        language
        value
      }
    }
  }
`)

const canteenMenuItemCardVendorDataFragment = graphql(`
  fragment CanteenMenuItemCard_VendorDataFragment on Vendor {
    id
    logo
    companyName
    currency
  }
`)

type Props = {
  itemData: FragmentType<typeof canteenMenuItemCardMenuItemFragment>
  userData: FragmentType<typeof canteenMenuItemCardUserDataFragment>
  vendorData: FragmentType<typeof canteenMenuItemCardVendorDataFragment>
  showHighlighted?: boolean
  onSeeMoreClick: (itemId: string) => void
  onAddToCart: () => void
}

const CCMenuItemCard: React.FC<Props> = ({
  userData: userDataProp,
  itemData: itemDataProp,
  vendorData: vendorDataProp,
  showHighlighted,
  onSeeMoreClick,
  onAddToCart,
}) => {
  const userData = useFragment(canteenMenuItemCardUserDataFragment, userDataProp)
  const {
    liveMenuItem,
    quantityRemaining,
    liveVariation: ccVariation,
  } = useFragment(canteenMenuItemCardMenuItemFragment, itemDataProp)
  const vendorData = useFragment(canteenMenuItemCardVendorDataFragment, vendorDataProp)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const handleSeeMoreClick = () => {
    onSeeMoreClick(liveMenuItem._id)
  }

  return (
    <ItemCard
      showHighlighted={showHighlighted}
      vendorAvatar={vendorData.logo}
      vendorName={vendorData.companyName}
      imageUrl={liveMenuItem.image}
      imageTitle={liveMenuItem.name}
      onClick={() => handleSeeMoreClick()}
    >
      <CardHeader title={liveMenuItem.name} />
      <CardContent>
        <Stack direction="column" spacing={1}>
          <Typography variant="body2">{liveMenuItem.shortDescription}</Typography>
          <Typography color="info.main" variant="body2">
            {typeof quantityRemaining === 'number' &&
            quantityRemaining > 0 &&
            quantityRemaining < 10
              ? `Only ${quantityRemaining} left`
              : null}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={0.5} direction="row">
              <DietaryRow
                isComboItem={Boolean(liveMenuItem.isCombo)}
                maxToShow={3}
                subAllergens={liveMenuItem.subAllergens}
                allergens={liveMenuItem.allergens}
                dietaries={liveMenuItem.dietaries}
              />
            </Stack>
            {!hidePrices ? (
              <Typography variant="body1">
                {numberFormat((ccVariation?.priceCustomerFacing || 0) / 100)}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions onClick={(e) => e.stopPropagation()}>
        <Stack width="100%" direction="row-reverse" alignItems="center" spacing={0.5}>
          {typeof quantityRemaining === 'number' && quantityRemaining <= 0 ? (
            <Button variant="contained" size="small" disabled>
              Sold out
            </Button>
          ) : (
            <IconButton
              onClick={() => {
                if (liveMenuItem.options && optionsAreValid(liveMenuItem.options, {})) {
                  onAddToCart()
                } else {
                  handleSeeMoreClick()
                }
              }}
            >
              <AddShoppingCart color="primary" />
            </IconButton>
          )}
          <Button
            onClick={() => handleSeeMoreClick()}
            size="small"
            variant="contained"
            color="primary"
          >
            See more
          </Button>
        </Stack>
      </CardActions>
    </ItemCard>
  )
}

export default CCMenuItemCard
